import { KnownImpressionTypes } from '../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';
import type {
  TemplatingItemDetailRawData,
  TemplatingItemSummaryRawData,
} from './types';

type HasPath = { path: string };

export type Folder = {
  type: 'folder';
  path: string;
};

export type TemplatingItem = {
  type: 'template' | 'compositeReportDefinition';
  id: string;
  durableId: string;
  description?: string;
  path: string;
  updatedAt: Date;
  createdAt: Date;
  lastUpdatedBy: string;
};

export type CreateItem<T extends TemplatingItem> = Omit<
  T,
  'id' | 'durableId' | 'updatedAt' | 'createdAt' | 'lastUpdatedBy'
>;

export type TemplateSummary = TemplatingItem & {
  type: 'template';
  impressionType?: KnownImpressionTypes;
};

export type CompositeReportDefinitionSummary = TemplatingItem & {
  type: 'compositeReportDefinition';
};

export type TemplatingItemSummary =
  | TemplateSummary
  | CompositeReportDefinitionSummary;

export type TemplateDetail = TemplateSummary & {
  content: string;
};

export type CompositeReportDefinitionDetail =
  CompositeReportDefinitionSummary & {
    sections: { templateId: string }[];
  };

export type TemplatingItemDetail =
  | TemplateDetail
  | CompositeReportDefinitionDetail;

export type TemplateNotFound = {
  type: 'templateNotFound';
  durableId: string;
};

const createFolder = (path: string): Folder => ({
  type: 'folder',
  path: path.endsWith('/') || path === '' ? path : path + '/',
});

const isFolder = (item: HasPath): item is Folder => item.path.endsWith('/');

const pathParts = (path: string) => path.split('/').filter(Boolean);

const folderPathFromParts = (parts: string[]) => [...parts, ''].join('/');

const templatingItemPathFromParts = (parts: string[]) => parts.join('/');

/**
 * Get the parent folder of a folder or templating item
 * @param item - The folder or templating item
 * @returns The parent folder
 */
const getParentFolder = (item: HasPath): Folder => {
  const parts = pathParts(item.path).slice(0, -1);
  return createFolder(folderPathFromParts(parts));
};

/**
 * Replace the name of a folder or templating item
 * @param item - The folder or templating item
 * @param name - The name to add
 * @returns The folder or templating item with the name added
 */
const withName = <T extends HasPath>(item: T, name: string): T => {
  const parts = pathParts(item.path).slice(0, -1);
  const pathConstructor = isFolder(item)
    ? folderPathFromParts
    : templatingItemPathFromParts;
  return { ...item, path: pathConstructor([...parts, name]) };
};

/**
 * Get the name of a folder or templating item
 * @param item - The folder or templating item
 * @returns The name
 */
const getName = (item: HasPath): string => {
  return pathParts(item.path).pop() ?? '';
};

/**
 * Move a templating item to a folder
 * @param item - The item to move
 * @param folder - The folder
 * @returns The item with the new path
 */
const moveToFolder = <T extends HasPath>(item: T, destination: Folder): T => {
  const folderParts = pathParts(destination.path);
  const pathConstructor = isFolder(item)
    ? folderPathFromParts
    : templatingItemPathFromParts;
  return { ...item, path: pathConstructor([...folderParts, getName(item)]) };
};

/**
 * Create a template
 * @param name - The name of the template
 * @param folder - The folder to move the template to
 * @returns The template
 */
const createNewTemplate = ({
  name,
  folder,
  impressionType,
}: {
  name: string;
  folder: Folder;
  impressionType?: KnownImpressionTypes;
}): CreateItem<TemplateDetail> => {
  const template = {
    type: 'template' as const,
    durableId: '',
    path: name,
    content: '',
    impressionType,
  };
  return moveToFolder(template, folder);
};

/**
 * Create a composite report definition
 * @param name - The name of the composite report definition
 * @param folder - The folder to move the composite report definition to
 * @returns The composite report definition
 */

const createNewCompositeReportDefinition = ({
  name,
  folder,
}: {
  name: string;
  folder: Folder;
}): CreateItem<CompositeReportDefinitionDetail> => {
  const compositeReportDefinition = {
    type: 'compositeReportDefinition' as const,
    durableId: '',
    path: name,
    sections: [],
  };
  return moveToFolder(compositeReportDefinition, folder);
};

/**
 * Create a templating item
 * @param name - The name of the templating item
 * @param folder - The folder to move the templating item to
 * @returns The templating item
 */
const createNewTemplatingItem = ({
  type,
  name,
  folder,
  impressionType,
}: {
  type: TemplatingItem['type'];
  name: string;
  folder: Folder;
  impressionType?: KnownImpressionTypes;
}) => {
  const templatingItem =
    type === 'template'
      ? createNewTemplate({ name, folder, impressionType })
      : createNewCompositeReportDefinition({ name, folder });
  return moveToFolder(templatingItem, folder);
};

/**
 * Create a templating item summary from raw data
 * @param rawData - The raw data
 * @returns The templating item summary
 */
const templatingItemSummaryFromRawData = (
  rawData: TemplatingItemSummaryRawData,
): TemplatingItemSummary | Folder => {
  if (rawData.type === 'folder') {
    return createFolder(rawData.path);
  } else {
    return {
      type: rawData.type,
      id: rawData.id,
      durableId: rawData.durableId,
      path: rawData.path,
      description: rawData.description ?? '',
      updatedAt: new Date(rawData.updatedAt),
      createdAt: new Date(rawData.createdAt),
      lastUpdatedBy: rawData.lastUpdatedBy,
      ...(rawData.type === 'template'
        ? { impressionType: rawData.impressionType }
        : {}),
    };
  }
};

/**
 * Create a templating item detail from raw data
 * @param rawData - The raw data
 * @returns The templating item detail
 */
const templatingItemDetailFromRawData = (
  rawData: TemplatingItemDetailRawData,
): TemplateDetail | CompositeReportDefinitionDetail => {
  const item: Omit<TemplatingItem, 'type'> = {
    id: rawData.id,
    durableId: rawData.durableId,
    path: rawData.path,
    description: rawData.description ?? '',
    updatedAt: new Date(rawData.updatedAt),
    createdAt: new Date(rawData.createdAt),
    lastUpdatedBy: rawData.lastUpdatedBy,
  };
  if (rawData.type === 'template') {
    return {
      ...item,
      type: rawData.type,
      content: rawData.content,
    };
  } else {
    return {
      ...item,
      type: rawData.type,
      sections: rawData.sections,
    };
  }
};

/**
 * Check if two templating items have the same path
 * @param itemA - The first templating item
 * @param itemB - The second templating item
 * @returns True if the paths are the same, false otherwise
 */
const hasSamePath = (itemA: HasPath, itemB: HasPath): boolean => {
  return itemA.path === itemB.path;
};

/**
 * Get the level of a templating item
 * @param item - The item
 * @returns The level of the item
 */
const getLevel = (item: HasPath): number => {
  return pathParts(item.path).length - 1;
};

/**
 * Check if an item is a child of a folder
 * @param folder - The folder
 * @param item - The item
 * @returns True if the item is a child of the folder, false otherwise
 */
const isChildOf = (folder: Folder, item: HasPath): boolean => {
  return item.path.startsWith(folder.path);
};

export const rootFolder = createFolder('');

export const ModelUtils = {
  createFolder,
  createNewTemplate,
  createNewCompositeReportDefinition,
  createNewTemplatingItem,
  templatingItemDetailFromRawData,
  templatingItemSummaryFromRawData,
  getParentFolder,
  withName,
  getName,
  moveToFolder,
  hasSamePath,
  getLevel,
  isChildOf,
};
